import { AuthenticationManager } from '@frontend/authentication-v2';
import { setCookie } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { getCorrectLocale } from '@frontend/lang';
import { ToastUtil } from '@frontend/toast-utils';
import { InviteClient } from '@frontend/user/api';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface AcceptUserInviteFormValues {
    first_name: string;
    last_name: string;
    password: string;
    confirm_password: string;
    mobile: string;
}

export const useAcceptUserInvite = () => {
    const { hash } = useParams<{ hash: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const intl = useIntl();

    const [formValues, setFormValues] = useState<AcceptUserInviteFormValues>({
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: '',
        mobile: ''
    });
    const [passwordValid, setPasswordValid] = useState(false);
    const [confirmPasswordValid, setConfirmPasswordValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isValidHash = useMemo(() => {
        if (!hash) return false;
        return UUID_REGEX.test(hash);
    }, [hash]);

    const passwordsMatch = formValues.password === formValues.confirm_password;

    const submitDisabled = useMemo(
        () =>
            isSubmitting ||
            !formValues.first_name.trim() ||
            !formValues.last_name.trim() ||
            !passwordValid ||
            !confirmPasswordValid ||
            !formValues.password ||
            !formValues.confirm_password ||
            !passwordsMatch,
        [
            confirmPasswordValid,
            formValues.confirm_password,
            formValues.first_name,
            formValues.last_name,
            formValues.password,
            isSubmitting,
            passwordValid,
            passwordsMatch
        ]
    );

    useEffect(() => {
        const language = searchParams.get('language');
        if (!language) return;

        const locale = getCorrectLocale(language);
        setCookie('language', locale.valueOf(), 365);
        window.dispatchEvent(new CustomEvent('languageChanged'));
    }, [searchParams]);

    const onSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
        e?.preventDefault();
        if (!hash || submitDisabled) return;

        setIsSubmitting(true);
        try {
            const includeAccessToken = AuthenticationManager.getInstance().hasValidToken();
            await InviteClient.acceptUserInvite(
                {
                    hash,
                    first_name: formValues.first_name.trim(),
                    last_name: formValues.last_name.trim(),
                    password: formValues.password,
                    mobile: formValues.mobile.trim() || null
                },
                includeAccessToken
            );

            ToastUtil.success(
                intl.formatMessage({
                    id: 'platform.standalone.accept-user-invite.success-title',
                    description: 'Toast title shown after successfully accepting a user invite',
                    defaultMessage: 'Invitation accepted'
                }),
                intl.formatMessage({
                    id: 'platform.standalone.accept-user-invite.success-message',
                    description: 'Toast message shown after successfully accepting a user invite',
                    defaultMessage: 'Your account has been created. You can now log in.'
                })
            );

            if (includeAccessToken) {
                await AuthenticationManager.getInstance().doLogout();
            }

            navigate('/');
        } catch (error) {
            ErrorHandler.handleError({ error: error as Error });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        hash: hash ?? null,
        isValidHash,
        formValues,
        setFormValues,
        passwordValid,
        setPasswordValid,
        confirmPasswordValid,
        setConfirmPasswordValid,
        passwordsMatch,
        submitDisabled,
        isSubmitting,
        onSubmit
    };
};
